import { useMemo } from "react";

function calculateDuration(durationInSeconds) {
  const totalHours = Math.floor(durationInSeconds / 3600);
  const totalMinutes = Math.floor((durationInSeconds % 3600) / 60);
  const totalSeconds = Math.floor(durationInSeconds % 60);

  const pad = function (num) {
    return String(num).padStart(2, "0");
  };

  const duration = [pad(totalHours), pad(totalMinutes), pad(totalSeconds)].join(
    ":"
  );

  return duration;
}

function isEmptyMessage(message) {
  return message === "";
}

function isCall(message) {
  const isQuestionEmpty = isEmptyMessage(message.q.trim());
  const isAnswerEmpty = isEmptyMessage(message.a.trim());
  const isWhatsappCallDataAvailable = Boolean(message?.whatsappCallData);

  return isWhatsappCallDataAvailable && isQuestionEmpty && isAnswerEmpty;
}

function processCallId(callIdString) {
  const [_, callMarker, callId] = callIdString.split("::");

  return { callMarker, callId };
}

// Old (pre turn-metrics): function generateMessageViewModel(message) {
//   return {
//     type: "message",
//     message,
//   };
// }
// New (turn metrics): keep the original index in conversation.messages so the view can join
// per-turn metrics by message index.
function generateMessageViewModel(message, index) {
  return {
    type: "message",
    message,
    index,
  };
}

function generateCallViewModel() {
  return {
    type: "call",
    callId: null,
    messages: [],
    start: null,
    end: null,
    duration: null,
    direction: null,
  };
}

function processCallMessage(state, message) {
  const { whatsappCallData } = message;
  const { callMarker } = processCallId(whatsappCallData.callId);

  if (callMarker === "start") {
    return processCallStartMessage(state, message);
  }

  return processCallEndMessage(state, message);
}

function processCallStartMessage(state, message) {
  const { whatsappCallData } = message;
  const { callId } = processCallId(whatsappCallData.callId);

  const processingCallViewModel = generateCallViewModel();

  processingCallViewModel.start = message.aTimestamp;
  processingCallViewModel.direction = whatsappCallData.direction;
  processingCallViewModel.duration = whatsappCallData?.duration;
  processingCallViewModel.callId = callId;

  state.isCallProcessing = true;

  return {
    ...state,
    processingCallViewModel,
  };
}

function processCallEndMessage(state, message) {
  state.processingCallViewModel.end = message.aTimestamp;
  state.processingCallViewModel.duration = calculateDuration(
    message.whatsappCallData.duration
  );

  state.isCallProcessing = false;

  state.calls.push(state.processingCallViewModel);
  state.messages.push(state.processingCallViewModel);

  return {
    ...state,
    processingCallViewModel: generateCallViewModel(),
  };
}

// Old (pre turn-metrics): function processMessage(state, message) {
//   const messageViewModel = generateMessageViewModel(message);
//   state.messages.push(messageViewModel);
//
//   return state;
// }
function processMessage(state, message, index) {
  const messageViewModel = generateMessageViewModel(message, index);
  state.messages.push(messageViewModel);

  return state;
}

function groupMessagesAndCalls(messagesWithCalls) {
  const { messages, calls } = messagesWithCalls.reduce(
    // Old (pre turn-metrics): function (acc, message) {
    // New (turn metrics): the reduce index becomes the message's index into
    // conversation.messages so the view can join per-turn metrics.
    function (acc, message, index) {
      if (isCall(message)) {
        return processCallMessage(acc, message);
      }

      if (acc.isCallProcessing) {
        acc.processingCallViewModel.messages.push({ type: "message", message });
        return acc;
      }

      // Old (pre turn-metrics): return processMessage(acc, message);
      return processMessage(acc, message, index);
    },
    {
      messages: [],
      calls: [],
      isCallProcessing: false,
      processingCallViewModel: null,
    }
  );

  return { messages, calls };
}

export default function useWhatsappMessageViewModel(whatsappMessages) {
  const { messages, calls } = useMemo(
    function () {
      return groupMessagesAndCalls(whatsappMessages);
    },
    [whatsappMessages]
  );

  return { messages, calls };
}
